import React from 'react';
import './CreatorCard.css';

function CreatorCard({ creator }) {
  const formatFollowers = (count) => {
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
    return count || 0;
  };

  // Support both new schema (instagram_*) and old schema
  const getFollowersCount = () => {
    return creator.instagram_followers_count || creator.followers_count || 0;
  };

  const getEngagementRate = () => {
    const rate = creator.instagram_engagement_rate || 
                 creator.historical_performance?.avg_engagement_rate ||
                 0;
    return parseFloat(rate).toFixed(1);
  };

  const getUsername = () => {
    return creator.instagram_username || 
           creator.username || 
           creator.name?.toLowerCase().replace(/\s/g, '_') || 
           'creator';
  };

  const getProfilePicture = () => {
    return creator.instagram_profile_picture || 
           creator.profile_picture_url || 
           getRandomAvatar(creator.name);
  };

  const getNiches = () => {
    // Combine from both new and old schema
    const niches = [
      ...(creator.instagram_detected_niches || []),
      ...(creator.creator_preferences_content_categories || []),
      ...(creator.content_niche || [])
    ];
    // Remove duplicates and return unique values
    return [...new Set(niches)].length > 0 ? [...new Set(niches)] : ['Lifestyle'];
  };

  const getLocation = () => {
    return creator.creator_preferences_target_locations?.[0] || 
           creator.city?.[0] || 
           creator.geo?.[0] || 
           'India';
  };

  const getPricing = () => {
    const minPrice = creator.creator_preferences_rate_per_post || 
                     creator.pricing_expectation?.min_base_price || 
                     5000;
    const maxPrice = creator.creator_preferences_rate_per_reel || 
                     creator.pricing_expectation?.max_base_price || 
                     minPrice * 2;
    return { min: minPrice, max: maxPrice };
  };

  const getPlatformIcon = (platform) => {
    switch(platform?.toLowerCase()) {
      case 'instagram':
        return '📸';
      case 'youtube':
        return '▶️';
      case 'tiktok':
        return '🎵';
      case 'twitter':
        return '🐦';
      default:
        return '📱';
    }
  };

  const getRandomAvatar = (name) => {
    const colors = ['6366f1', '8b5cf6', 'ec4899', '14b8a6', 'f59e0b', '10b981'];
    const color = colors[Math.abs(name?.charCodeAt(0) || 0) % colors.length];
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Creator')}&background=${color}&color=fff&size=200`;
  };

  const niches = getNiches();
  const displayNiches = niches.slice(0, 3);
  const pricing = getPricing();

  return (
    <div className="creator-card">
      <div className="card-header">
        <img 
          src={getProfilePicture()}
          alt={creator.name}
          className="creator-avatar"
          onError={(e) => e.target.src = getRandomAvatar(creator.name)}
        />
        <div className="platform-badge">
          {getPlatformIcon(creator.platform)}
        </div>
      </div>
      
      <div className="card-body">
        <h3 className="creator-name">{creator.name || 'Creator'}</h3>
        <p className="creator-handle">@{getUsername()}</p>
        
        <div className="creator-stats">
          <div className="stat-item">
            <span className="stat-value">{formatFollowers(getFollowersCount())}</span>
            <span className="stat-label">Followers</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{getEngagementRate()}%</span>
            <span className="stat-label">Engagement</span>
          </div>
        </div>
        
        <div className="creator-niches">
          {displayNiches.map((niche, idx) => (
            <span key={idx} className="niche-tag">
              {typeof niche === 'string' ? niche.charAt(0).toUpperCase() + niche.slice(1) : niche}
            </span>
          ))}
        </div>
        
        <div className="creator-location">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          {typeof getLocation() === 'string' ? getLocation().charAt(0).toUpperCase() + getLocation().slice(1) : getLocation()}
        </div>
      </div>
      
      <div className="card-footer">
        <span className="creator-price">
          ₹{pricing.min?.toLocaleString()} - ₹{pricing.max?.toLocaleString()}
        </span>
        <button className="btn-view-profile">View Profile</button>
      </div>
    </div>
  );
}

export default CreatorCard;
